import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppEmpty from '@nxsys/components/AppEmpty';
import { useGetUpcomingPayment } from '@nxsys/hooks/api/dashboard';
import { useViewPayslipById } from '@nxsys/hooks/api/payroll/usePayrollApi';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Skeleton } from 'antd';
import moment from 'moment';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { IRELAND } from '@nxsys/store/constants/keys';
import upcomingPaymentEuro from '@assets/images/upcoming-payment-euro.svg';
import upcomingPaymentPound from '@assets/images/upcoming-payment-pound.svg';

type UpcomingPaymentProps = {
  subcontractorId: string;
  isCisOrNonCis: boolean;
};

const UpcomingPayment = ({
  subcontractorId,
  isCisOrNonCis,
}: UpcomingPaymentProps) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const navigate = useNavigateWithQuery();
  const {
    value: upcomingPaymentValue,
    loading,
    error,
    run: fetchUpcomingPayment,
  } = useGetUpcomingPayment();
  const { loading: downloadPayslipLoading, run: downloadPayslip } =
    useViewPayslipById();

  const data = upcomingPaymentValue?.data ?? null;

  useEffect(() => {
    if (subcontractorId) {
      fetchUpcomingPayment(subcontractorId);
    }
  }, [subcontractorId]);

  const getIsUpcoming = () => {
    if (!data?.paymentDate) return true;
    return (
      moment(data.paymentDate).isSame(moment(), 'day') ||
      moment(data.paymentDate).isAfter(moment(), 'day')
    );
  };

  const getTitle = () => {
    if (!data?.paymentDate) return 'Upcoming Payment';
    if (getIsUpcoming()) return 'Upcoming Payment';
    return isCisOrNonCis ? 'Last Self Bill' : 'Last Payslip';
  };

  const getSubtitle = () => {
    if (!data) return 'No upcoming payments yet';
    if (!data.paymentDate) return "You've got an upcoming payment";
    if (getIsUpcoming()) return "You've got an upcoming payment";
    return isCisOrNonCis
      ? 'View your last self bill'
      : 'View your last payslip';
  };

  const getTaxPeriodText = () => {
    if (!data) return '';
    const isMonthly = data.payFrequency === 'MONTHLY';
    const frequency = isMonthly ? 'month' : 'week';
    const period = isMonthly ? data.monthConfig : data.taxPeriod;
    return `Tax ${frequency} ${period}`;
  };

  const getPercentageChange = () => {
    if (!data || data.netPayPercentageChange === null) return null;
    const value = Number(data.netPayPercentageChange);
    if (value > 0) return `+${value.toFixed(2)}%`;
    if (value < 0) return `${value.toFixed(2)}%`;
    return '0%';
  };

  const handleViewPayslip = () => {
    if (!data?.selfBillRefId) return;
    navigate(`/payslips/${data.selfBillRefId}`);
  };

  const handleDownloadPayslip = async () => {
    if (!data?.selfBillRefId) return;
    try {
      const result = await downloadPayslip(data.selfBillRefId);
      if (result?.data) {
        window.open(result.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCompare = () => {
    const event = new CustomEvent('ai-chat:open-and-send', {
      detail:
        'Compare my latest payroll with the previous one and show me what changed.',
    });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <AppCard className="p-[20px] h-full">
        <Skeleton active />
      </AppCard>
    );
  }

  if (error) {
    return (
      <AppCard className="p-[20px] h-full">
        <div className="text-[12px] text-neutral-500">
          Loading upcoming payment failed
        </div>
      </AppCard>
    );
  }

  if (!data) {
    return (
      <AppCard className="p-[20px] h-full">
        <img
          src={
            payrollCountry === IRELAND
              ? upcomingPaymentEuro
              : upcomingPaymentPound
          }
          alt="Upcoming Payment"
          className="mb-[16px] w-full object-cover rounded-[8px]"
        />
        <AppEmpty />
      </AppCard>
    );
  }

  const percentageChange = getPercentageChange();
  const isPositive =
    data.netPayPercentageChange !== null &&
    Number(data.netPayPercentageChange) >= 0;

  return (
    <AppCard className="p-[20px] h-full">
      <img
        src={
          payrollCountry === IRELAND
            ? upcomingPaymentEuro
            : upcomingPaymentPound
        }
        alt="Upcoming Payment"
        className="mb-[16px] w-full h-[80px] object-cover rounded-[8px]"
      />
      <div className="text-[16px] font-[500] mb-[6px]">{getTitle()}</div>
      <div className="text-[12px] font-[400] text-neutral-500">
        {getSubtitle()}
      </div>
      <Divider variant="dashed" className="my-[16px]" />
      <div className="text-[12px] font-[400] text-neutral-500 mb-[2px]">
        {getTaxPeriodText()}
      </div>
      <div className="text-[16px] font-[500] mb-[8px]">Total Net Pay</div>
      <div className="flex items-center gap-x-[8px] mb-[8px]">
        <div className="text-[18px] font-[600] text-primary-600">
          {formatMoney(
            parseFloat(data.net) || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
        {percentageChange && (
          <div
            className={`text-[12px] font-[400] ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            {percentageChange}
          </div>
        )}
        {percentageChange && (
          <button
            onClick={handleCompare}
            className="flex items-center gap-x-[4px] text-[11px] text-neutral-400 hover:text-primary-600 hover:border-primary-600 cursor-pointer bg-transparent border border-neutral-200 rounded-[6px] px-[6px] py-[2px] transition-colors"
          >
            <InfoCircleOutlined style={{ fontSize: 11 }} />
            What's changed?
          </button>
        )}
      </div>
      <Divider variant="dashed" className="my-[16px]" />
      <div className="flex items-center gap-x-[8px] w-full">
        <AppButton
          variant="outlined"
          name={isCisOrNonCis ? 'View Self Bill' : 'View Payslip'}
          leftIcon={<Icons.IconEye />}
          className="flex-1"
          onClick={handleViewPayslip}
        />
        <AppButton
          variant="outlined"
          leftIcon={<Icons.IconDownload />}
          onClick={handleDownloadPayslip}
          loading={downloadPayslipLoading}
        />
      </div>
    </AppCard>
  );
};

export default UpcomingPayment;
